'use client'

import { createContext, useContext, useRef, useState, type ReactNode } from 'react'
import { useTranslations } from 'next-intl'
import Image from 'next/image'

type ScreenshotZoomProps = {
  src: string
  alt: string
  width?: number
  height?: number
  className?: string
}

/**
 * Default thumbnail size, matching the 390x710 shape every 2.0 app screenshot
 * is captured at. Anything on a different shape must pass its own
 * width/height, or next/image warns that the declared aspect and the rendered
 * one disagree.
 */
const THUMB_W = 148
const THUMB_H = 269

/** Width the screenshot grows to. Matches what the old overlay showed. */
const ZOOMED_W = 320

/** How far a press may travel before it counts as a drag, not a tap. */
const DRAG_SLOP = 10

/**
 * Shared open/closed state for a run of screenshots that replace each other in
 * place, like the features slider. Without it each screenshot owns its own
 * state, so swiping to the next one lands you on a closed screenshot and you
 * have to open every single one by hand.
 *
 * Optional: a screenshot with no provider above it keeps its own state.
 */
const ZoomContext = createContext<
  [boolean, React.Dispatch<React.SetStateAction<boolean>>] | null
>(null)

export function ScreenshotZoomProvider({ children }: { children: ReactNode }) {
  const state = useState(false)
  return <ZoomContext.Provider value={state}>{children}</ZoomContext.Provider>
}

/**
 * A feature screenshot that grows in place, the way the hero strip does, rather
 * than throwing a fullscreen overlay over the page.
 *
 * Zoomed, it takes the full width of its row; the feature's icon and text wrap
 * underneath it (the row is `flex-wrap`), which is the whole point — the
 * screenshot gets the space, and the words stay with it instead of being
 * covered up.
 *
 * The label under it is the same handwritten aside the hero strip uses, so the
 * two zoomable things on the page invite the same gesture in the same voice.
 *
 * The toggle label lives here too. It used to be a second instance of this
 * component rendered over in the text column, which meant two pieces of state
 * that could disagree about whether the same screenshot was open.
 */
export function ScreenshotZoom({
  src,
  alt,
  width = THUMB_W,
  height = THUMB_H,
  className = '',
}: ScreenshotZoomProps) {
  const t = useTranslations('features')
  // Both are always called; only one is used. A provider above means the
  // slider owns the state and it survives swiping to the next feature.
  const shared = useContext(ZoomContext)
  const own = useState(false)
  const [zoomed, setZoomed] = shared ?? own

  /*
   * Where the press started. A swipe across the slider usually begins on the
   * screenshot, since zoomed it is most of what is under your thumb, and a
   * button fires its click at the end of that swipe. Without this, swiping to
   * the next feature would snap the screenshot shut on the way out.
   */
  const pressedAt = useRef<{ x: number; y: number } | null>(null)

  const handleClick = (e: React.MouseEvent) => {
    const start = pressedAt.current
    pressedAt.current = null
    // No pointerdown means the keyboard did this, which is always a real press.
    if (start && Math.hypot(e.clientX - start.x, e.clientY - start.y) > DRAG_SLOP) return
    setZoomed(!zoomed)
  }

  return (
    /* Full width on a phone so the words follow the screenshot down; on
       desktop there is room for both, so it keeps its own width and the text
       stays in the column beside it. */
    <div className={`${zoomed ? 'w-full md:w-auto md:shrink-0' : 'shrink-0'} ${className}`}>
      <button
        type="button"
        onPointerDown={(e) => {
          pressedAt.current = { x: e.clientX, y: e.clientY }
        }}
        onClick={handleClick}
        aria-expanded={zoomed}
        aria-label={zoomed ? t('zoomCloseLabel') : t('zoomInLabel')}
        className="block cursor-pointer overflow-hidden rounded-[12px] border-2 border-black transition-[width] duration-300 ease-out motion-reduce:transition-none"
        style={{ width: zoomed ? ZOOMED_W : width, maxWidth: '100%' }}
      >
        <Image
          src={src}
          alt={alt}
          width={width}
          height={height}
          sizes={`${ZOOMED_W}px`}
          className="w-full h-auto"
        />
      </button>

      {/* The label does what it says. aria-hidden with tabIndex -1 because the
          screenshot above is the same control, already named for a screen
          reader: this is a second way to hit it with a thumb, not a second
          thing to tab through. */}
      <button
        type="button"
        aria-hidden="true"
        tabIndex={-1}
        onPointerDown={(e) => {
          pressedAt.current = { x: e.clientX, y: e.clientY }
        }}
        onClick={handleClick}
        className="mt-[8px] block w-fit cursor-pointer origin-left -rotate-2 select-none text-[26px] leading-none text-[#6B8E6B]"
        style={{ fontFamily: 'var(--font-caveat), cursive' }}
      >
        {zoomed ? t('zoomCloseLabel') : t('zoomInLabel')}
      </button>
    </div>
  )
}
